// Email-link sign-in (#3928, founder pick A of the sign-in mocks, 2026-10-11).
//
// The second way in, beside Google: Firebase emails a one-time link, the
// student taps it, and the link signs them in. No password exists anywhere:
// the provider is passwordless on the project (auth_methods.py refuses to
// call it ready otherwise).
//
// The rules this module holds:
//   - It is offered only when get-auth-methods says the live Auth config is
//     ready (provider on, mail verified from our own domain). Until then the
//     app is exactly as before: Google only. A team or automated browser may
//     preview it (the dark path) with ?signin_preview=email.
//   - One account per email (the project's setting): a link for an address
//     that already signed in with Google signs into THAT account, never a
//     second one. Firebase decides this; nothing here creates users.
//   - The link proves the inbox, not the browser. Opened in the browser that
//     asked, the email kept here finishes it; opened anywhere else, the
//     student types the address again (Firebase's own guidance: a forwarded
//     or intercepted link must not sign someone into another account).
//   - The sign-in carry survives it. The landing conversation, the unlocked
//     read and the destination live in this TAB's sessionStorage, and the
//     Mail app opens the link in a NEW tab: so the tab's carry is copied
//     beside the email at send time and put back at the finish. Across
//     browsers (asked in Instagram, opened in Safari) only the destination
//     rides in the link. Not the read: anyone can ask Firebase to send a
//     real link to anyone, with a carry they wrote, and a genuine read token
//     from the attacker's own visit would land in the victim's account
//     (security review, #3928).
import { isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink, getAdditionalUserInfo } from 'firebase/auth';
import { auth } from '../firebase';
import { track, EVENTS, isOurBrowser } from './analytics';
import { fetchAuthMethods, sendSignInCode, verifySignInCode } from './api';
import { isIOS, isStandalone } from './push';
import { heldAttribution } from './attribution';
import { signinEntry } from '../content/landingIds';
import { THREAD_KEY } from './astraChatThread';
import { CARRY_KEY, READ_CARRY_KEY, STAND_SAVE_KEY } from './astraCarry';
import { MOMENT_KEY, heldMomentEntry, loadMoment, restoreMoment } from './signupMoments';
import { MOMENT_IDS } from '../content/signupMoments';
import { PENDING_DEST_KEY, isInternalPath, rememberDestination } from '../utils/postSignIn';
import { ATTEMPT_RE, endSignInAttempt, inAppBrowserName, signInAttemptId, signInAttemptProps } from './signInTrouble';

export const FINISH_PATH = '/sign-in/finish';
// The email and the tab's carry, kept for the finish. localStorage: the link
// opens in a new tab of the same browser.
export const STORED_KEY = 'stratia_email_link_v1';
// Long enough to find the email; past it the student just types the address.
export const STORED_TTL_MS = 6 * 60 * 60 * 1000;
// "Send again" waits this long, so a double tap never sends two links.
export const RESEND_AFTER_MS = 60 * 1000;
const PREVIEW_PARAM = 'signin_preview';
const PREVIEW_KEY = 'stratia_signin_preview';
// ?signin_preview=phone (#5084): the email-first sheet on a team device
// while phone_email_first is off (a signed-out page cannot be allowlisted
// by account). It previews the email link too: the sheet needs both.
const PHONE_PREVIEW_KEY = 'stratia_signin_preview_phone';
// ?signin_preview=more (#5154): the Apple and Facebook buttons on a team
// device while more_sign_in_options is off. It previews the flag only: a
// provider the project's Auth config has not turned on is never drawn, so
// the preview never shows a button that cannot work.
const MORE_PREVIEW_KEY = 'stratia_signin_preview_more';
const AVAILABLE_KEY = 'stratia_auth_methods_v1';
const AVAILABLE_TTL_MS = 5 * 60 * 1000;
// The tab's sessionStorage keys that make up the sign-in carry. Imported, never
// retyped: a renamed key would otherwise stop carrying without a sound. A
// function, read at use: a module that mocks one of these must still load.
export const sessionCarryKeys = () => [THREAD_KEY, READ_CARRY_KEY, CARRY_KEY, STAND_SAVE_KEY, PENDING_DEST_KEY, MOMENT_KEY];
// What may ride in the link itself (cross-browser): a few hundred bytes.
const URL_CARRY_MAX = 1800;
const SCHOOL_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** A snapshot's moment record, read through the one validator. */
const momentOf = (raw) => (typeof raw === 'string' ? loadMoment(raw) : null);

// ── is it offered? ──────────────────────────────────────────────────────────

let available = null;          // true | false | null (not known yet)
let phoneFirst = null;         // phone_email_first on for everyone (#5084): true | false | null
let more = null;               // #5154: {flag, apple, facebook} | null (not known yet)
let inflight = null;
// The unit suite never reaches the network: every page with a Start free
// would otherwise call the live route from jsdom. Tests that need an answer
// set one with _setAvailabilityFetcher.
let fetcher = import.meta.env.MODE === 'test' ? async () => null : fetchAuthMethods;
export function _setAvailabilityFetcher(fn) { fetcher = fn || fetchAuthMethods; }

function readCachedAnswer(now = Date.now()) {
    try {
        const r = JSON.parse(sessionStorage.getItem(AVAILABLE_KEY) || 'null');
        if (r && typeof r.ok === 'boolean' && typeof r.t === 'number' && now - r.t < AVAILABLE_TTL_MS && now >= r.t) {
            return { ok: r.ok, phone: r.phone === true, more: r.more && typeof r.more === 'object' ? r.more : null };
        }
    } catch { /* storage off */ }
    return null;
}

function readCachedAvailability(now = Date.now()) {
    const r = readCachedAnswer(now);
    if (r && phoneFirst === null) phoneFirst = r.phone;
    if (r && more === null && r.more) more = r.more;
    return r ? r.ok : null;
}

/** The dark path: a team or automated browser that asked for it this tab. */
export function previewRequested() {
    try {
        const ask = new URLSearchParams(window.location.search).get(PREVIEW_PARAM);
        if (ask === 'email' || ask === 'phone') sessionStorage.setItem(PREVIEW_KEY, '1');
        if (ask === 'phone') sessionStorage.setItem(PHONE_PREVIEW_KEY, '1');
        if (ask === 'more') sessionStorage.setItem(MORE_PREVIEW_KEY, '1');
        return sessionStorage.getItem(PREVIEW_KEY) === '1' && isOurBrowser();
    } catch { return false; }
}

function morePreviewRequested() {
    try {
        const ask = new URLSearchParams(window.location.search).get(PREVIEW_PARAM);
        if (ask === 'more') sessionStorage.setItem(MORE_PREVIEW_KEY, '1');
        return sessionStorage.getItem(MORE_PREVIEW_KEY) === '1' && isOurBrowser();
    } catch { return false; }
}

/** Which of the new providers the sheet may draw (#5154): each one needs
 *  the project's Auth config to have it on (the founder's setup), AND
 *  more_sign_in_options on for everyone or a team device's ?signin_preview=more.
 *  {apple, facebook}; unknown is all false: today's sheet. Google is not
 *  here: it is always on, outside an app's browser. */
export function extraProvidersOn() {
    if (more === null) readCachedAvailability();
    const flag = more?.flag === true || morePreviewRequested();
    return { apple: flag && more?.apple === true, facebook: flag && more?.facebook === true };
}

function phonePreviewRequested() {
    try { return previewRequested() && sessionStorage.getItem(PHONE_PREVIEW_KEY) === '1'; } catch { return false; }
}

/** Whether an iPhone or iPad Safari sheet leads with email (#5084): only
 *  with the email link on AND phone_email_first on for everyone (the
 *  server's answer), or on a team device's phone preview. Unknown is off:
 *  today's sheet. */
export function phoneEmailFirstOn() {
    if (phonePreviewRequested()) return true;
    if (!emailSignInAvailable()) return false;
    if (phoneFirst === null) readCachedAvailability();
    return phoneFirst === true;
}

/** The answer on the email link as it stands: true, false, or null while
 *  get-auth-methods has not answered (the in-app race, #5084). */
export function emailSignInAnswer() {
    if (previewRequested()) return true;
    if (available === null) available = readCachedAvailability();
    return available;
}

/** Synchronous, for a click handler: true only when already known to be on. */
export function emailSignInAvailable() {
    if (previewRequested()) return true;
    if (available === null) available = readCachedAvailability();
    return available === true;
}

/** Ask once per page life (cached five minutes per tab). Never rejects. */
export function loadEmailSignInAvailability() {
    if (previewRequested()) return Promise.resolve(true);
    const cached = readCachedAvailability();
    if (cached !== null) { available = cached; return Promise.resolve(cached); }
    if (!inflight) {
        inflight = fetcher().then((r) => {
            available = r?.email_link === true;
            phoneFirst = r?.phone_email_first === true;
            more = { flag: r?.more_sign_in_options === true, apple: r?.apple === true, facebook: r?.facebook === true };
            if (r) {
                try {
                    sessionStorage.setItem(AVAILABLE_KEY, JSON.stringify({ ok: available, phone: phoneFirst, more, t: Date.now() }));
                } catch { /* storage off */ }
            }
            return available;
        }).catch(() => { available = false; phoneFirst = false; more = null; return false; })
            .finally(() => { inflight = null; });
    }
    return inflight;
}

/** True when the server read says the link mail goes out from our own
 *  domain (never on the preview path alone, where Firebase's default sender
 *  may still be in use). */
export function emailSenderIsOurs() {
    if (available === null) available = readCachedAvailability();
    return available === true;
}

export function _resetAvailability() { available = null; phoneFirst = null; more = null; inflight = null; }

// ── sending ─────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const normalizeEmail = (v) => String(v || '').trim().toLowerCase();
export const looksLikeEmail = (v) => EMAIL_RE.test(normalizeEmail(v)) && normalizeEmail(v).length <= 254;

function sessionSnapshot() {
    const out = {};
    for (const k of sessionCarryKeys()) {
        try { const v = sessionStorage.getItem(k); if (v != null) out[k] = v; } catch { /* storage off */ }
    }
    return out;
}

function b64url(text) {
    const bytes = new TextEncoder().encode(text);
    let bin = '';
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64url(s) {
    const pad = s.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(pad + '==='.slice((pad.length + 3) % 4));
    return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}

/** What rides in the link for a finish in another browser: the destination
 *  and the attempt id (#5084, a random id for the funnel), and a sign-up
 *  moment's id and its catalog school ids (#5195: the moment labels the new
 *  account's events, and /start OFFERS those schools in its empty fields,
 *  never adds them, so a crafted link can do no more than pre-fill a field
 *  the student must still confirm). Never the conversation (words the
 *  student typed stay in this browser), never the read (a crafted link must
 *  not act on someone's account), never the GPA, never the address. '' when
 *  there is nothing. */
export function urlCarry(snapshot = sessionSnapshot(), attempt = signInAttemptId()) {
    const dest = snapshot[PENDING_DEST_KEY];
    const out = {};
    if (isInternalPath(dest)) out.d = dest;
    if (attempt && ATTEMPT_RE.test(attempt)) out.a = attempt;
    const moment = momentOf(snapshot[MOMENT_KEY]);
    if (moment) {
        out.m = moment.moment;
        if (moment.school_ids.length) out.s = moment.school_ids;
    }
    if (!Object.keys(out).length) return '';
    const enc = b64url(JSON.stringify(out));
    return enc.length > URL_CARRY_MAX ? '' : enc;
}

export function finishUrl(carry) {
    const base = `${window.location.origin}${FINISH_PATH}`;
    return carry ? `${base}?c=${carry}` : base;
}

/**
 * Email the link. Resolves {ok: true} or {ok: false, code}. Never throws.
 * `flow`: 'sheet' (the normal sheet), 'in_app_browser' or 'back'.
 */
export async function sendEmailLink(rawEmail, { entry, flow = 'sheet', resend = false } = {}) {
    const email = normalizeEmail(rawEmail);
    const app = inAppBrowserName();
    const props = { method: 'email', flow, entry: signinEntry(entry), ...(app ? { app } : {}), ...signInAttemptProps() };
    if (!looksLikeEmail(email)) return { ok: false, code: 'auth/invalid-email' };
    try { if (!resend) track(EVENTS.SIGNIN_STARTED, props); } catch { /* analytics never blocks */ }
    const snapshot = sessionSnapshot();
    if (isHomeScreenApp()) return sendCode(email, snapshot, props, resend);
    try {
        await sendSignInLinkToEmail(auth, email, { url: finishUrl(urlCarry(snapshot)), handleCodeInApp: true });
    } catch (error) {
        try {
            track(EVENTS.SIGNIN_FAILED, {
                method: 'email', code: error?.code || 'unknown', cancelled: false,
                reason: 'email_send', flow, ...(app ? { app } : {}), ...signInAttemptProps(),
            });
        } catch { /* analytics never blocks */ }
        return { ok: false, code: error?.code || 'unknown' };
    }
    try { localStorage.setItem(STORED_KEY, JSON.stringify({ email, t: Date.now(), session: snapshot, attempt: signInAttemptId() })); } catch { /* private mode: the finish asks for the email */ }
    try { track(EVENTS.SIGNIN_LINK_SENT, { ...props, resend: Boolean(resend) }); } catch { /* analytics never blocks */ }
    return { ok: true };
}

// ── finishing ───────────────────────────────────────────────────────────────

/** The stored request, or null (none, unreadable, or older than the TTL). */
export function storedRequest(now = Date.now()) {
    let raw = null;
    try { raw = localStorage.getItem(STORED_KEY); } catch { return null; }
    if (!raw) return null;
    try {
        const r = JSON.parse(raw);
        if (r && typeof r.email === 'string' && typeof r.t === 'number' && now - r.t < STORED_TTL_MS && now >= r.t) return r;
    } catch { /* unreadable: removed below */ }
    // Expired or unreadable: it holds the tab's conversation, so it goes
    // rather than waiting on a shared computer (reviewer, #3928).
    clearStoredRequest();
    return null;
}

export const clearStoredRequest = () => { try { localStorage.removeItem(STORED_KEY); } catch { /* storage off */ } };

export function isEmailLink(href) {
    try { return isSignInWithEmailLink(auth, href); } catch { return false; }
}

/** The carry a link brought from another browser, validated, or null. */
export function readUrlCarry(href) {
    try {
        const c = new URL(href).searchParams.get('c');
        if (!c || c.length > URL_CARRY_MAX || !/^[A-Za-z0-9_-]+$/.test(c)) return null;
        const parsed = JSON.parse(fromB64url(c));
        if (!parsed || typeof parsed !== 'object') return null;
        // The attempt id is an analytics join key only: a crafted one can do
        // no more than mislabel one event, and the pattern keeps it an id.
        const out = {};
        if (isInternalPath(parsed.d)) out.d = parsed.d;
        if (typeof parsed.a === 'string' && ATTEMPT_RE.test(parsed.a)) out.a = parsed.a;
        if (MOMENT_IDS.includes(parsed.m)) {
            out.m = parsed.m;
            const ids = Array.isArray(parsed.s) ? parsed.s.filter((i) => typeof i === 'string' && SCHOOL_ID_RE.test(i)).slice(0, 4) : [];
            if (ids.length) out.s = ids;
        }
        return Object.keys(out).length ? out : null;
    } catch { return null; }
}

/** Put the carry back for this tab before the post-sign-in steps read it.
 *  Never over something this tab already holds. */
export function restoreCarry({ stored, href }) {
    const put = (k, v) => {
        try { if (sessionStorage.getItem(k) == null) sessionStorage.setItem(k, v); } catch { /* storage off */ }
    };
    if (stored && stored.session && typeof stored.session === 'object') {
        for (const k of sessionCarryKeys()) {
            if (typeof stored.session[k] === 'string') put(k, stored.session[k]);
        }
        return;
    }
    const c = readUrlCarry(href);
    if (c?.d) rememberDestination(c.d);
    if (c?.m) restoreMoment({ moment: c.m, schoolIds: c.s || [] });
}

/**
 * Finish the sign-in from the link. `email`: the address typed on this
 * page, or null to use the stored one. Resolves {user} or {error}; never
 * throws. `otherDevice`: the email had to be typed (no stored request).
 */
export async function finishEmailLink(href, { email = null, otherDevice = false } = {}) {
    const stored = storedRequest();
    const address = normalizeEmail(email || stored?.email);
    if (!looksLikeEmail(address)) return { error: { code: 'auth/invalid-email' } };
    try {
        const result = await signInWithEmailLink(auth, address, href);
        // The carry came from the browser that asked only when its email matches.
        const ours = stored && normalizeEmail(stored.email) === address ? stored : null;
        restoreCarry({ stored: ours, href });
        clearStoredRequest();
        try {
            const attr = heldAttribution();
            const device = otherDevice ? 'other' : 'same';
            const attempt = linkAttempt(ours, href);
            endSignInAttempt();
            // #5195: the sign-up moment the carry brought back names the entry.
            const moment = heldMomentEntry();
            const entry = moment ? { entry: moment } : {};
            track(EVENTS.SIGNIN_COMPLETED, { method: 'email', device, ...entry, ...attr, ...signInAttemptProps(attempt) });
            if (getAdditionalUserInfo(result)?.isNewUser) {
                track(EVENTS.SIGNED_UP, { method: 'email', ...entry, ...attr });
                track(EVENTS.GA4_SIGN_UP, { method: 'email', ...attr });
            }
        } catch { /* analytics never blocks sign-in */ }
        return { user: result.user };
    } catch (error) {
        try {
            track(EVENTS.SIGNIN_FAILED, {
                method: 'email', code: error?.code || 'unknown', cancelled: false,
                reason: emailFailureReason(error?.code), flow: 'email_link',
                device: otherDevice ? 'other' : 'same', ...signInAttemptProps(linkAttempt(stored, href)),
            });
        } catch { /* analytics never blocks */ }
        return { error: { code: error?.code || 'unknown' } };
    }
}

/** The attempt a link belongs to: the request this browser kept, else the
 *  id the link carried from the browser that asked (#5084). */
function linkAttempt(stored, href) {
    const kept = typeof stored?.attempt === 'string' ? stored.attempt : null;
    return kept || readUrlCarry(href)?.a || null;
}

export function emailFailureReason(code) {
    switch (code) {
        case 'auth/invalid-action-code':
        case 'auth/expired-action-code':
            return 'link_expired';
        case 'auth/invalid-email':
            return 'email_mismatch';
        case 'auth/network-request-failed':
            return 'network';
        default:
            return 'other';
    }
}

/** What the finish page says for a failure: what happened, then what to do. */
export function finishTroubleCopy(code) {
    switch (emailFailureReason(code)) {
        case 'link_expired':
            return "This sign-in link has expired or was already used. Send yourself a new one.";
        case 'email_mismatch':
            return "That isn't the email this link was sent to. Check the address and try again.";
        case 'network':
            return "We couldn't reach the sign-in service. Check your connection, then try again.";
        default:
            return 'Something went wrong on our side. Please try again in a moment.';
    }
}

/** What the sheet says when a link could not be sent. */
export function sendTroubleCopy(code) {
    switch (code) {
        case 'auth/invalid-email':
            return 'That email address looks incomplete. Check it and try again.';
        case 'auth/too-many-requests':
        case 'auth/quota-exceeded':
            return 'Too many sign-in emails were asked for just now. Wait a few minutes, then try again.';
        case 'auth/network-request-failed':
            return "We couldn't reach the sign-in service. Check your connection, then try again.";
        default:
            return 'Something went wrong on our side. Please try again in a moment.';
    }
}

// ── the Home Screen app's code (#4189, founder pick A, 2026-10-14) ──────────
//
// An iPhone Home Screen app keeps its own storage, and an emailed link opens
// in Safari: Safari gets signed in, this app does not, and this app is the
// only place an iPhone can turn on Astra Today's notifications. So a request
// made HERE is sent by our own route (email_code_signin.py): the same
// Firebase link as the email's button, and a 6-digit code under it that this
// app takes. The code only works with the request id kept here. Safari and
// desktop never reach this path. Installed desktop and Android apps share
// the browser's storage, so the link already signs them in: only an iPhone
// or iPad Home Screen app gets the code.

/** True only inside an iPhone/iPad Home Screen app. */
export const isHomeScreenApp = () => isIOS() && isStandalone();

export const CODE_KEY = 'stratia_email_code_v1';
// In memory only, this page life: never stored.
let acceptedLink = null;
export function _resetAcceptedLink() { acceptedLink = null; }

/** The pending code request kept by this app, or null. */
export function storedCodeRequest(now = Date.now()) {
    try {
        const r = JSON.parse(localStorage.getItem(CODE_KEY) || 'null');
        if (r && typeof r.email === 'string' && typeof r.rid === 'string' && typeof r.t === 'number'
            && now - r.t < STORED_TTL_MS && now >= r.t) return r;
    } catch { /* unreadable */ }
    try { localStorage.removeItem(CODE_KEY); } catch { /* storage off */ }
    return null;
}

const clearCodeRequest = () => { try { localStorage.removeItem(CODE_KEY); } catch { /* storage off */ } };

// The server's outcome words, mapped to the sheet's codes.
const SEND_ERRORS = { rate_limited: 'auth/too-many-requests', invalid: 'auth/invalid-email', network: 'auth/network-request-failed' };

async function sendCode(email, snapshot, props, resend) {
    const res = await sendSignInCode(email, urlCarry(snapshot) || undefined);
    if (!res.ok) {
        const code = SEND_ERRORS[res.error] || 'unknown';
        try { track(EVENTS.SIGNIN_FAILED, { method: 'email_code', code, cancelled: false, reason: 'email_send', flow: props.flow, ...signInAttemptProps() }); } catch { /* analytics never blocks */ }
        return { ok: false, code };
    }
    try { localStorage.setItem(CODE_KEY, JSON.stringify({ email, rid: res.request_id, t: Date.now(), session: snapshot, attempt: signInAttemptId() })); } catch { /* the code then cannot be checked; the sheet says so */ }
    try { track(EVENTS.SIGNIN_LINK_SENT, { ...props, code: true, resend: Boolean(resend) }); } catch { /* analytics never blocks */ }
    return { ok: true, code: true };
}

/** What a failed code says (the mocks' error table): what happened, then
 *  what to do. `dead`: the code cannot be used again, so the sheet offers a
 *  new one instead of the box. */
export const CODE_TROUBLE = {
    wrong_code: { dead: false, copy: "That code doesn't match the one we emailed. Check it and try again." },
    too_many_tries: { dead: true, copy: "That's too many tries for this code. Send yourself a new one." },
    expired: { dead: true, copy: 'This code has expired or was already used. Send yourself a new one.' },
    used_in_safari: { dead: true, copy: 'This code was used up when the link opened in Safari. Send yourself a new one to sign in here.' },
    network: { dead: false, copy: "We couldn't reach the sign-in service. Check your connection, then try again." },
    other: { dead: false, copy: 'Something went wrong on our side. Please try again in a moment.' },
};

export const codeTrouble = (reason) => CODE_TROUBLE[reason] || CODE_TROUBLE.other;

/**
 * Check the code and finish the sign-in with Firebase's own
 * signInWithEmailLink, so the account rules, the new-user events and the
 * password guard all run as for the link. Resolves {user} or {reason}.
 */
export async function finishWithCode(rawCode) {
    const req = storedCodeRequest();
    const code = String(rawCode || '').replace(/\D/g, '');
    if (!req) return { reason: 'expired' };
    if (code.length !== 6) return { reason: 'wrong_code' };
    const fail = (reason) => {
        try { track(EVENTS.SIGNIN_FAILED, { method: 'email_code', code: reason, cancelled: false, reason, flow: 'home_screen', ...signInAttemptProps(linkAttempt(req, null)) }); } catch { /* analytics never blocks */ }
        if (codeTrouble(reason).dead) clearCodeRequest();
        return { reason };
    };
    // A code the server already accepted (then the network dropped before
    // Firebase finished) is spent there: retry with the link it gave, never
    // a second check that would now say "expired" (reviewer, #4189).
    const held = acceptedLink && acceptedLink.rid === req.rid && acceptedLink.code === code ? acceptedLink : null;
    const res = held ? { ok: true, link: held.link } : await verifySignInCode(req.rid, req.email, code);
    if (res.ok && !held) acceptedLink = { rid: req.rid, code, link: res.link };
    if (!res.ok) return fail(CODE_TROUBLE[res.error] ? res.error : (res.error === 'invalid' ? 'wrong_code' : res.error === 'network' ? 'network' : 'other'));
    try {
        const result = await signInWithEmailLink(auth, req.email, res.link);
        restoreCarry({ stored: req, href: res.link });
        clearCodeRequest();
        acceptedLink = null;
        try {
            const attr = heldAttribution();
            const attempt = linkAttempt(req, null);
            endSignInAttempt();
            track(EVENTS.SIGNIN_COMPLETED, { method: 'email_code', device: 'same', ...attr, ...signInAttemptProps(attempt) });
            if (getAdditionalUserInfo(result)?.isNewUser) {
                track(EVENTS.SIGNED_UP, { method: 'email', ...attr });
                track(EVENTS.GA4_SIGN_UP, { method: 'email', ...attr });
            }
        } catch { /* analytics never blocks sign-in */ }
        return { user: result.user };
    } catch (error) {
        // The code matched but its link was already spent: Safari opened it.
        const reason = emailFailureReason(error?.code) === 'link_expired' ? 'used_in_safari'
            : emailFailureReason(error?.code) === 'network' ? 'network' : 'other';
        if (reason !== 'network') acceptedLink = null;
        return fail(reason);
    }
}

/** True when this link was asked for from the Home Screen app (the server
 *  marks it), so the browser it opened in should not quietly use it up. */
export function linkFromHomeScreen(href) {
    try {
        const u = new URL(href);
        if (u.searchParams.get('hs') === '1') return true;
        // Firebase's action page may hand the continue URL over whole.
        const cont = u.searchParams.get('continueUrl');
        return Boolean(cont) && new URL(cont).searchParams.get('hs') === '1';
    } catch { return false; }
}
